import React, { useState, useMemo, useEffect } from 'react';
import './RecipePage.css';

export default function RecipeCalculator({
    initialQty150 = 10,
    initialQty180 = 1,
    bills = [],
    batchEligibleBills = [],
    availableDates = [],
    dateFilter = 'all',
    setDateFilter = () => {},
    slotFilter = 'all',
    setSlotFilter = () => {},
    statusFilter = 'all_active',
    setStatusFilter = () => {},
    orderFilter = 'all',
    setOrderFilter = () => {},
    batchSummary = null,
    formatDateShort = (d) => d,
    countCakesInBill = () => 0,
    todayStr = '',
    tomorrowStr = '',
    isBillPaid = () => false
}) {
    // Mode: 'mixed' (Gộp cả 150g & 180g theo đơn thực tế) | 'single' (Chỉ 1 cỡ bánh)
    const [calcMode, setCalcMode] = useState('mixed');

    const activeBills = (batchEligibleBills && batchEligibleBills.length > 0)
        ? batchEligibleBills
        : ((batchSummary && batchSummary.candidateBillsForBatch) ? batchSummary.candidateBillsForBatch : bills);
    const candidateOrders = (batchSummary && batchSummary.candidateBillsForBatch)
        ? batchSummary.candidateBillsForBatch
        : activeBills;

    // Selected Filling: 'thapcam' | 'all' | or filling name from bills
    const [selectedFillingKey, setSelectedFillingKey] = useState('thapcam');

    // Crust View Filter: 'both' (cả vỏ nướng & dẻo) | 'nuong' | 'deo'
    const [crustViewMode, setCrustViewMode] = useState('both');

    // Sync source indicator: 'bill' | 'custom'
    const [syncSource, setSyncSource] = useState('bill');

    const [toast, setToast] = useState('');
    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    };

    // Build the list of available fillings from batchSummary
    const availableFillings = useMemo(() => {
        const list = [];

        // 1. Option Toàn mẻ (Tất cả vị)
        const totalAll = batchSummary?.totalCakes || 0;
        const totalAll150 = batchSummary?.batch150?.totalCakes || 0;
        const totalAll180 = batchSummary?.batch180?.totalCakes || 0;
        const nuongAll150 = batchSummary?.batch150?.totalNuong || 0;
        const deoAll150 = batchSummary?.batch150?.totalDeo || 0;
        const nuongAll180 = batchSummary?.batch180?.totalNuong || 0;
        const deoAll180 = batchSummary?.batch180?.totalDeo || 0;
        const totalNuongAll = batchSummary?.totalNuong || 0;
        const totalDeoAll = batchSummary?.totalDeo || 0;
        const totalEggsAll = batchSummary?.totalEggs || 0;

        list.push({
            key: 'all',
            name: 'Tất cả các vị bánh (Toàn mẻ)',
            shortName: 'Toàn mẻ',
            icon: 'fa-layer-group',
            totalQty: totalAll,
            qty150: totalAll150,
            qty180: totalAll180,
            nuong150: nuongAll150,
            deo150: deoAll150,
            nuong180: nuongAll180,
            deo180: deoAll180,
            totalNuong: totalNuongAll,
            totalDeo: totalDeoAll,
            eggQty: totalEggsAll,
            isNoFilling: false,
            isThapCam: false,
            isAll: true,
            orders: []
        });

        if (batchSummary?.allConsolidatedFillings && batchSummary.allConsolidatedFillings.length > 0) {
            batchSummary.allConsolidatedFillings.forEach(f => {
                const fName = f.filling || '';
                const lower = fName.toLowerCase();
                const isTC = lower.includes('thập cẩm') || lower.includes('thap cam');
                const isNF = f.isNoFilling || lower.includes('không nhân') || lower.includes('khong nhan') || lower.includes('thuần vỏ');

                list.push({
                    key: fName,
                    name: isTC ? 'Nhân Thập Cẩm Hạt Ít Ngọt' : (isNF ? `${fName} (Thuần vỏ)` : `Nhân ${fName}`),
                    shortName: fName,
                    icon: isTC ? 'fa-mortar-pestle' : (isNF ? 'fa-bread-slice' : 'fa-bowl-rice'),
                    totalQty: f.totalQty || 0,
                    qty150: f.qty150 || 0,
                    qty180: f.qty180 || 0,
                    nuong150: f.nuong150 || 0,
                    deo150: f.deo150 || 0,
                    nuong180: f.nuong180 || 0,
                    deo180: f.deo180 || 0,
                    totalNuong: (f.nuong150 || 0) + (f.nuong180 || 0),
                    totalDeo: (f.deo150 || 0) + (f.deo180 || 0),
                    eggQty: f.eggQty || 0,
                    isNoFilling: isNF,
                    isThapCam: isTC,
                    isAll: false,
                    orders: f.orders || []
                });
            });
        } else {
            // Fallback standards if batchSummary isn't loaded
            list.push({
                key: 'thapcam',
                name: 'Nhân Thập Cẩm Hạt Ít Ngọt',
                shortName: 'Thập Cẩm',
                icon: 'fa-mortar-pestle',
                totalQty: 12,
                qty150: 12,
                qty180: 0,
                nuong150: 10,
                deo150: 2,
                nuong180: 0,
                deo180: 0,
                totalNuong: 10,
                totalDeo: 2,
                eggQty: 0,
                isNoFilling: false,
                isThapCam: true,
                isAll: false,
                orders: []
            });
            list.push({
                key: 'Đậu xanh vani',
                name: 'Nhân Đậu xanh vani',
                shortName: 'Đậu xanh vani',
                icon: 'fa-bowl-rice',
                totalQty: 0,
                qty150: 0,
                qty180: 0,
                nuong150: 0,
                deo150: 0,
                nuong180: 0,
                deo180: 0,
                totalNuong: 0,
                totalDeo: 0,
                eggQty: 0,
                isNoFilling: false,
                isThapCam: false,
                isAll: false,
                orders: []
            });
        }

        return list;
    }, [batchSummary]);

    // Current selected filling object
    const currentFilling = useMemo(() => {
        if (!selectedFillingKey || selectedFillingKey === 'all') {
            return availableFillings.find(f => f.isAll) || availableFillings[0];
        }
        if (selectedFillingKey === 'thapcam') {
            const foundTC = availableFillings.find(f => f.isThapCam);
            if (foundTC) return foundTC;
        }
        return availableFillings.find(f => f.key === selectedFillingKey) || availableFillings[0];
    }, [selectedFillingKey, availableFillings]);

    // Detailed quantities state:
    // Nướng 150g, Dẻo 150g, Nướng 180g, Dẻo 180g
    const [nuong150, setNuong150] = useState(10);
    const [deo150, setDeo150] = useState(2);
    const [nuong180, setNuong180] = useState(0);
    const [deo180, setDeo180] = useState(0);

    // Single mode controls
    const [singleSize, setSingleSize] = useState(150); // 150 | 180
    const [singleType, setSingleType] = useState('nuong'); // 'nuong' | 'deo'
    const [singleQty, setSingleQty] = useState(10);

    // Auto-sync whenever the selected filling, date filter, or batchSummary updates
    useEffect(() => {
        if (currentFilling) {
            setNuong150(currentFilling.nuong150);
            setDeo150(currentFilling.deo150);
            setNuong180(currentFilling.nuong180);
            setDeo180(currentFilling.deo180);

            if (singleSize === 150) {
                setSingleQty(singleType === 'nuong' ? (currentFilling.nuong150 || 1) : (currentFilling.deo150 || 1));
            } else {
                setSingleQty(singleType === 'nuong' ? (currentFilling.nuong180 || 1) : (currentFilling.deo180 || 1));
            }
            setSyncSource('bill');
        }
    }, [currentFilling, dateFilter, slotFilter, statusFilter, orderFilter, singleSize, singleType]);

    const formatSelectedDate = (dateVal) => {
        if (!dateVal || dateVal === 'all') return 'Tất cả các mẻ / ngày trả';
        if (dateVal === 'today') return `Hôm nay (${formatDateShort(todayStr)})`;
        if (dateVal === 'tomorrow') return `Ngày mai (${formatDateShort(tomorrowStr)})`;
        return `Ngày ${formatDateShort(dateVal)}`;
    };

    const formatGram = (val) => {
        if (val === undefined || val === null || isNaN(val)) return '0g';
        const num = Number(val);
        const rounded = Math.round(num * 10) / 10;
        return rounded % 1 === 0 ? `${rounded}g` : `${rounded.toFixed(1)}g`;
    };

    const formatRoundGram = (val) => {
        if (val === undefined || val === null || isNaN(val)) return '0g';
        return `${Math.round(Number(val))}g`;
    };

    // Core Recipe Calculations
    const calcData = useMemo(() => {
        let n150 = 0;
        let d150 = 0;
        let n180 = 0;
        let d180 = 0;

        if (calcMode === 'mixed') {
            n150 = Math.max(0, parseInt(nuong150, 10) || 0);
            d150 = Math.max(0, parseInt(deo150, 10) || 0);
            n180 = Math.max(0, parseInt(nuong180, 10) || 0);
            d180 = Math.max(0, parseInt(deo180, 10) || 0);
        } else {
            const sq = Math.max(1, parseInt(singleQty, 10) || 1);
            if (singleSize === 150) {
                if (singleType === 'nuong') n150 = sq; else d150 = sq;
            } else {
                if (singleType === 'nuong') n180 = sq; else d180 = sq;
            }
        }

        const totalNuong150 = n150;
        const totalDeo150 = d150;
        const total150 = n150 + d150;

        const totalNuong180 = n180;
        const totalDeo180 = d180;
        const total180 = n180 + d180;

        const totalNuong = n150 + n180;
        const totalDeo = d150 + d180;
        const totalCakes = total150 + total180;

        const isNF = currentFilling.isNoFilling;
        const isTC = currentFilling.isThapCam;
        const isAll = currentFilling.isAll;

        // --- 1. VỎ BÁNH NƯỚNG ---
        // Chuẩn: Bánh nướng 150g dùng 55g vỏ (nếu không nhân: 150g vỏ)
        // Bánh nướng 180g dùng 65g vỏ (nếu không nhân: 180g vỏ)
        const unitCrustNuong150 = isNF ? 150.0 : 55.0;
        const unitCrustNuong180 = isNF ? 180.0 : 65.0;

        // Tỷ lệ chuẩn vỏ bánh nướng: Bột mì 56%, Nước đường nướng 34%, Dầu ăn 6%, Bơ đậu phộng 4%
        const single150_crustNuong = {
            botMi: Number((unitCrustNuong150 * 0.56).toFixed(1)),
            nuocDuong: Number((unitCrustNuong150 * 0.34).toFixed(1)),
            dauAn: Number((unitCrustNuong150 * 0.06).toFixed(1)),
            boDauPhong: Number((unitCrustNuong150 * 0.04).toFixed(1)),
            totalCrust: unitCrustNuong150
        };

        const single180_crustNuong = {
            botMi: Number((unitCrustNuong180 * 0.56).toFixed(1)),
            nuocDuong: Number((unitCrustNuong180 * 0.34).toFixed(1)),
            dauAn: Number((unitCrustNuong180 * 0.06).toFixed(1)),
            boDauPhong: Number((unitCrustNuong180 * 0.04).toFixed(1)),
            totalCrust: unitCrustNuong180
        };

        const crustNuongTotal = {
            qty150: totalNuong150,
            qty180: totalNuong180,
            totalCakes: totalNuong,
            sum150: totalNuong150 * unitCrustNuong150,
            sum180: totalNuong180 * unitCrustNuong180,
            totalWeight: (totalNuong150 * unitCrustNuong150) + (totalNuong180 * unitCrustNuong180),
            details150: {
                botMi: totalNuong150 * single150_crustNuong.botMi,
                nuocDuong: totalNuong150 * single150_crustNuong.nuocDuong,
                dauAn: totalNuong150 * single150_crustNuong.dauAn,
                boDauPhong: totalNuong150 * single150_crustNuong.boDauPhong,
            },
            details180: {
                botMi: totalNuong180 * single180_crustNuong.botMi,
                nuocDuong: totalNuong180 * single180_crustNuong.nuocDuong,
                dauAn: totalNuong180 * single180_crustNuong.dauAn,
                boDauPhong: totalNuong180 * single180_crustNuong.boDauPhong,
            },
            grandDetails: {
                botMi: (totalNuong150 * single150_crustNuong.botMi) + (totalNuong180 * single180_crustNuong.botMi),
                nuocDuong: (totalNuong150 * single150_crustNuong.nuocDuong) + (totalNuong180 * single180_crustNuong.nuocDuong),
                dauAn: (totalNuong150 * single150_crustNuong.dauAn) + (totalNuong180 * single180_crustNuong.dauAn),
                boDauPhong: (totalNuong150 * single150_crustNuong.boDauPhong) + (totalNuong180 * single180_crustNuong.boDauPhong),
            }
        };

        // --- 2. VỎ BÁNH DẺO ---
        // Chuẩn xưởng: Bánh dẻo 150g dùng 75g vỏ (tỷ lệ 1:1; hoặc không nhân 150g vỏ)
        // Bánh dẻo 180g dùng 90g vỏ (tỷ lệ 1:1; hoặc không nhân 180g vỏ)
        const unitCrustDeo150 = isNF ? 150.0 : 75.0;
        const unitCrustDeo180 = isNF ? 180.0 : 90.0;

        // Tỷ lệ vỏ dẻo: Bột bánh dẻo 33%, Nước đường bánh dẻo 65%, Dầu ăn 2%, Tinh dầu hoa bưởi (2-3 giọt)
        const single150_crustDeo = {
            botBanhDeo: Number((unitCrustDeo150 * 0.33).toFixed(1)),
            nuocDuong: Number((unitCrustDeo150 * 0.65).toFixed(1)),
            dauAn: Number((unitCrustDeo150 * 0.02).toFixed(1)),
            hoaBuoi: 2,
            totalCrust: unitCrustDeo150
        };

        const single180_crustDeo = {
            botBanhDeo: Number((unitCrustDeo180 * 0.33).toFixed(1)),
            nuocDuong: Number((unitCrustDeo180 * 0.65).toFixed(1)),
            dauAn: Number((unitCrustDeo180 * 0.02).toFixed(1)),
            hoaBuoi: 3,
            totalCrust: unitCrustDeo180
        };

        const crustDeoTotal = {
            qty150: totalDeo150,
            qty180: totalDeo180,
            totalCakes: totalDeo,
            sum150: totalDeo150 * unitCrustDeo150,
            sum180: totalDeo180 * unitCrustDeo180,
            totalWeight: (totalDeo150 * unitCrustDeo150) + (totalDeo180 * unitCrustDeo180),
            details150: {
                botBanhDeo: totalDeo150 * single150_crustDeo.botBanhDeo,
                nuocDuong: totalDeo150 * single150_crustDeo.nuocDuong,
                dauAn: totalDeo150 * single150_crustDeo.dauAn,
                hoaBuoi: totalDeo150 * single150_crustDeo.hoaBuoi,
            },
            details180: {
                botBanhDeo: totalDeo180 * single180_crustDeo.botBanhDeo,
                nuocDuong: totalDeo180 * single180_crustDeo.nuocDuong,
                dauAn: totalDeo180 * single180_crustDeo.dauAn,
                hoaBuoi: totalDeo180 * single180_crustDeo.hoaBuoi,
            },
            grandDetails: {
                botBanhDeo: (totalDeo150 * single150_crustDeo.botBanhDeo) + (totalDeo180 * single180_crustDeo.botBanhDeo),
                nuocDuong: (totalDeo150 * single150_crustDeo.nuocDuong) + (totalDeo180 * single180_crustDeo.nuocDuong),
                dauAn: (totalDeo150 * single150_crustDeo.dauAn) + (totalDeo180 * single180_crustDeo.dauAn),
                hoaBuoi: (totalDeo150 * single150_crustDeo.hoaBuoi) + (totalDeo180 * single180_crustDeo.hoaBuoi),
            }
        };

        // --- 3. ĐỊNH LƯỢNG NHÂN (FILLING) ---
        // Trọng lượng nhân từng bánh:
        // Nướng: 150g -> 95g nhân | 180g -> 115g nhân
        // Dẻo: 150g -> 75g nhân | 180g -> 90g nhân
        // Không nhân: 0g
        const fillingGram150 = isNF ? 0 : (n150 * 95 + d150 * 75);
        const fillingGram180 = isNF ? 0 : (n180 * 115 + d180 * 90);
        const totalFillingWeight = fillingGram150 + fillingGram180;

        // Công thức Thập Cẩm Ít Ngọt Chuẩn Tiệm (Giảm mứt, tăng hạt bùi, sốt không đường, không dùng tiêu)
        const single150_tc_dry = {
            mutSen: 7.5,
            mutBi: 3.5,
            mutGung: 1.5,
            hatDieu: 16.5,
            lacRang: 12.5,
            hatBiXanh: 13.5,
            hatDua: 11.5,
            vungTrang: 5.0,
            laChanh: 1.5,
            botBanhDeo: 7.0,
            sotTron: 15.0,
            totalFilling: 95.0,
        };

        const single180_tc_dry = {
            mutSen: 9.1,
            mutBi: 4.2,
            mutGung: 1.8,
            hatDieu: 20.0,
            lacRang: 15.1,
            hatBiXanh: 16.4,
            hatDua: 13.9,
            vungTrang: 6.1,
            laChanh: 1.8,
            botBanhDeo: 8.5,
            sotTron: 18.1,
            totalFilling: 115.0,
        };

        const single150_tc_sauce = {
            nuocLoc: 7.5,
            dauAn: 3.5,
            dauMe: 0.8,
            ruouMQL: 1.0,
            nuocTuong: 1.2,
            dauHao: 0.8,
            muoiTinh: 0.2,
            totalSauce: 15.0,
        };

        const single180_tc_sauce = {
            nuocLoc: 9.1,
            dauAn: 4.2,
            dauMe: 1.0,
            ruouMQL: 1.2,
            nuocTuong: 1.4,
            dauHao: 1.0,
            muoiTinh: 0.2,
            totalSauce: 18.1,
        };

        // Tính cho tổng bánh Thập Cẩm (quy chuẩn theo số bánh 150g và 180g)
        const countTC150 = total150;
        const countTC180 = total180;

        const tcTotal150 = {
            dry: {
                mutSen: countTC150 * single150_tc_dry.mutSen,
                mutBi: countTC150 * single150_tc_dry.mutBi,
                mutGung: countTC150 * single150_tc_dry.mutGung,
                hatDieu: countTC150 * single150_tc_dry.hatDieu,
                lacRang: countTC150 * single150_tc_dry.lacRang,
                hatBiXanh: countTC150 * single150_tc_dry.hatBiXanh,
                hatDua: countTC150 * single150_tc_dry.hatDua,
                vungTrang: countTC150 * single150_tc_dry.vungTrang,
                laChanh: countTC150 * single150_tc_dry.laChanh,
                botBanhDeo: countTC150 * single150_tc_dry.botBanhDeo,
                sotTron: countTC150 * single150_tc_dry.sotTron,
            },
            sauce: {
                nuocLoc: countTC150 * single150_tc_sauce.nuocLoc,
                dauAn: countTC150 * single150_tc_sauce.dauAn,
                dauMe: countTC150 * single150_tc_sauce.dauMe,
                ruouMQL: countTC150 * single150_tc_sauce.ruouMQL,
                nuocTuong: countTC150 * single150_tc_sauce.nuocTuong,
                dauHao: countTC150 * single150_tc_sauce.dauHao,
                muoiTinh: countTC150 * single150_tc_sauce.muoiTinh,
                totalSauce: countTC150 * single150_tc_sauce.totalSauce,
            }
        };

        const tcTotal180 = {
            dry: {
                mutSen: countTC180 * single180_tc_dry.mutSen,
                mutBi: countTC180 * single180_tc_dry.mutBi,
                mutGung: countTC180 * single180_tc_dry.mutGung,
                hatDieu: countTC180 * single180_tc_dry.hatDieu,
                lacRang: countTC180 * single180_tc_dry.lacRang,
                hatBiXanh: countTC180 * single180_tc_dry.hatBiXanh,
                hatDua: countTC180 * single180_tc_dry.hatDua,
                vungTrang: countTC180 * single180_tc_dry.vungTrang,
                laChanh: countTC180 * single180_tc_dry.laChanh,
                botBanhDeo: countTC180 * single180_tc_dry.botBanhDeo,
                sotTron: countTC180 * single180_tc_dry.sotTron,
            },
            sauce: {
                nuocLoc: countTC180 * single180_tc_sauce.nuocLoc,
                dauAn: countTC180 * single180_tc_sauce.dauAn,
                dauMe: countTC180 * single180_tc_sauce.dauMe,
                ruouMQL: countTC180 * single180_tc_sauce.ruouMQL,
                nuocTuong: countTC180 * single180_tc_sauce.nuocTuong,
                dauHao: countTC180 * single180_tc_sauce.dauHao,
                muoiTinh: countTC180 * single180_tc_sauce.muoiTinh,
                totalSauce: countTC180 * single180_tc_sauce.totalSauce,
            }
        };

        const tcGrandTotal = {
            dry: {
                mutSen: tcTotal150.dry.mutSen + tcTotal180.dry.mutSen,
                mutBi: tcTotal150.dry.mutBi + tcTotal180.dry.mutBi,
                mutGung: tcTotal150.dry.mutGung + tcTotal180.dry.mutGung,
                hatDieu: tcTotal150.dry.hatDieu + tcTotal180.dry.hatDieu,
                lacRang: tcTotal150.dry.lacRang + tcTotal180.dry.lacRang,
                hatBiXanh: tcTotal150.dry.hatBiXanh + tcTotal180.dry.hatBiXanh,
                hatDua: tcTotal150.dry.hatDua + tcTotal180.dry.hatDua,
                vungTrang: tcTotal150.dry.vungTrang + tcTotal180.dry.vungTrang,
                laChanh: tcTotal150.dry.laChanh + tcTotal180.dry.laChanh,
                botBanhDeo: tcTotal150.dry.botBanhDeo + tcTotal180.dry.botBanhDeo,
                sotTron: tcTotal150.dry.sotTron + tcTotal180.dry.sotTron,
            },
            sauce: {
                nuocLoc: tcTotal150.sauce.nuocLoc + tcTotal180.sauce.nuocLoc,
                dauAn: tcTotal150.sauce.dauAn + tcTotal180.sauce.dauAn,
                dauMe: tcTotal150.sauce.dauMe + tcTotal180.sauce.dauMe,
                ruouMQL: tcTotal150.sauce.ruouMQL + tcTotal180.sauce.ruouMQL,
                nuocTuong: tcTotal150.sauce.nuocTuong + tcTotal180.sauce.nuocTuong,
                dauHao: tcTotal150.sauce.dauHao + tcTotal180.sauce.dauHao,
                muoiTinh: tcTotal150.sauce.muoiTinh + tcTotal180.sauce.muoiTinh,
                totalSauce: tcTotal150.sauce.totalSauce + tcTotal180.sauce.totalSauce,
            },
            filling: (countTC150 * 95) + (countTC180 * 115)
        };

        return {
            nuong150: n150,
            deo150: d150,
            total150,
            nuong180: n180,
            deo180: d180,
            total180,
            totalNuong,
            totalDeo,
            totalCakes,
            unitCrustNuong150,
            unitCrustNuong180,
            unitCrustDeo150,
            unitCrustDeo180,
            single150_crustNuong,
            single180_crustNuong,
            single150_crustDeo,
            single180_crustDeo,
            crustNuongTotal,
            crustDeoTotal,
            totalDoughWeight: crustNuongTotal.totalWeight + crustDeoTotal.totalWeight,
            fillingGram150,
            fillingGram180,
            totalFillingWeight,
            single150_tc_dry,
            single180_tc_dry,
            single150_tc_sauce,
            single180_tc_sauce,
            tcTotal150,
            tcTotal180,
            tcGrandTotal,
            isNF,
            isTC,
            isAll
        };
    }, [calcMode, nuong150, deo150, nuong180, deo180, singleSize, singleType, singleQty, currentFilling]);

    // Copy to clipboard
    const handleCopy = () => {
        let text = `📋 [BẢNG CÂN NGUYÊN LIỆU CHI TIẾT XƯỞNG BÁNH]\n`;
        text += `Vị bánh: ${currentFilling.name.toUpperCase()}\n`;
        text += `Mẻ làm: ${calcData.totalCakes} bánh (${calcData.totalNuong} nướng · ${calcData.totalDeo} dẻo)\n`;
        text += `• Cỡ 150g: ${calcData.total150} chiếc (${calcData.nuong150} nướng · ${calcData.deo150} dẻo)\n`;
        text += `• Cỡ 180g: ${calcData.total180} chiếc (${calcData.nuong180} nướng · ${calcData.deo180} dẻo)\n`;
        text += `👉 TỔNG NGUYÊN LIỆU: ${formatRoundGram(calcData.totalFillingWeight)} nhân | ${formatRoundGram(calcData.crustNuongTotal.totalWeight)} vỏ nướng | ${formatRoundGram(calcData.crustDeoTotal.totalWeight)} vỏ dẻo\n\n`;

        if (calcData.totalNuong > 0) {
            text += `1. BỘT VỎ BÁNH NƯỚNG (Tổng ${calcData.totalNuong} bánh = ${formatRoundGram(calcData.crustNuongTotal.totalWeight)} bột vỏ):\n`;
            text += `- Bột mì: ${formatRoundGram(calcData.crustNuongTotal.grandDetails.botMi)}\n`;
            text += `- Nước đường bánh nướng: ${formatRoundGram(calcData.crustNuongTotal.grandDetails.nuocDuong)}\n`;
            text += `- Dầu ăn tinh luyện: ${formatRoundGram(calcData.crustNuongTotal.grandDetails.dauAn)}\n`;
            text += `- Bơ đậu phộng mịn: ${formatRoundGram(calcData.crustNuongTotal.grandDetails.boDauPhong)}\n\n`;
        }

        if (calcData.totalDeo > 0) {
            text += `2. BỘT VỎ BÁNH DẺO (Tổng ${calcData.totalDeo} bánh = ${formatRoundGram(calcData.crustDeoTotal.totalWeight)} bột vỏ):\n`;
            text += `- Bột bánh dẻo (nếp chín): ${formatRoundGram(calcData.crustDeoTotal.grandDetails.botBanhDeo)}\n`;
            text += `- Nước đường bánh dẻo: ${formatRoundGram(calcData.crustDeoTotal.grandDetails.nuocDuong)}\n`;
            text += `- Dầu ăn thực vật: ${formatRoundGram(calcData.crustDeoTotal.grandDetails.dauAn)}\n`;
            text += `- Tinh dầu hoa bưởi: ${calcData.crustDeoTotal.grandDetails.hoaBuoi} giọt\n\n`;
        }

        if (calcData.isTC) {
            text += `3. NGUYÊN LIỆU HẠT & MỨT THẬP CẨM (${formatRoundGram(calcData.tcGrandTotal.filling)}):\n`;
            text += `- Mứt sen: ${formatRoundGram(calcData.tcGrandTotal.dry.mutSen)}\n`;
            text += `- Mứt bí: ${formatRoundGram(calcData.tcGrandTotal.dry.mutBi)}\n`;
            text += `- Mứt gừng đỏ: ${formatRoundGram(calcData.tcGrandTotal.dry.mutGung)}\n`;
            text += `- Hạt điều rang: ${formatRoundGram(calcData.tcGrandTotal.dry.hatDieu)}\n`;
            text += `- Lạc rang: ${formatRoundGram(calcData.tcGrandTotal.dry.lacRang)}\n`;
            text += `- Hạt bí xanh: ${formatRoundGram(calcData.tcGrandTotal.dry.hatBiXanh)}\n`;
            text += `- Hạt dưa tách nõn: ${formatRoundGram(calcData.tcGrandTotal.dry.hatDua)}\n`;
            text += `- Vừng trắng rang: ${formatRoundGram(calcData.tcGrandTotal.dry.vungTrang)}\n`;
            text += `- Lá chanh thái chỉ: ${formatRoundGram(calcData.tcGrandTotal.dry.laChanh)}\n`;
            text += `- Bột bánh dẻo kết dính: ${formatRoundGram(calcData.tcGrandTotal.dry.botBanhDeo)}\n`;
            text += `- Nước sốt trộn không đường: ${formatRoundGram(calcData.tcGrandTotal.sauce.totalSauce)}\n`;
            text += `  + Nước lọc: ${formatRoundGram(calcData.tcGrandTotal.sauce.nuocLoc)}\n`;
            text += `  + Dầu ăn: ${formatRoundGram(calcData.tcGrandTotal.sauce.dauAn)}\n`;
            text += `  + Dầu mè: ${formatRoundGram(calcData.tcGrandTotal.sauce.dauMe)}\n`;
            text += `  + Rượu Mai Quế Lộ: ${formatRoundGram(calcData.tcGrandTotal.sauce.ruouMQL)}\n`;
            text += `  + Nước tương: ${formatRoundGram(calcData.tcGrandTotal.sauce.nuocTuong)}\n`;
            text += `  + Dầu hào: ${formatRoundGram(calcData.tcGrandTotal.sauce.dauHao)}\n`;
            text += `  + Muối tinh (ghìm ngọt): ${formatRoundGram(calcData.tcGrandTotal.sauce.muoiTinh)}\n`;
        } else if (!calcData.isNF) {
            text += `3. ĐỊNH LƯỢNG NHÂN CẦN CHUẨN BỊ: ${formatRoundGram(calcData.totalFillingWeight)} (~${(calcData.totalFillingWeight / 1000).toFixed(2)} kg)\n`;
            text += `- Bánh 150g: ${calcData.nuong150} nướng (${formatRoundGram(calcData.nuong150 * 95)}) · ${calcData.deo150} dẻo (${formatRoundGram(calcData.deo150 * 75)})\n`;
            text += `- Bánh 180g: ${calcData.nuong180} nướng (${formatRoundGram(calcData.nuong180 * 115)}) · ${calcData.deo180} dẻo (${formatRoundGram(calcData.deo180 * 90)})\n`;
            if (currentFilling.eggQty > 0) {
                text += `- Trứng muối: ${currentFilling.eggQty} quả lòng đỏ\n`;
            }
        } else {
            text += `3. BÁNH THUẦN VỎ KHÔNG NHÂN: 0g nhân. Bột vỏ chuẩn bị đầy đủ theo mục 1 & 2.\n`;
        }

        text += `\n---\nCookie Shop — Bộ Phận Bếp`;

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(() => {
                showToast(`Đã sao chép bảng cân công thức vị ${currentFilling.shortName}!`);
            }).catch(() => {
                showToast('Không thể sao chép tự động.');
            });
        }
    };

    return (
        <div className="recipe-page">
            {/* Header banner */}
            <div className="recipe-hero">
                <span className="recipe-hero-badge">
                    <i className="fa-solid fa-mortar-pestle" /> Công thức &amp; Định lượng bánh Cookie Shop
                </span>
                <h2 className="recipe-hero-title">
                    {calcData.isTC
                        ? 'Nhân Thập Cẩm Hạt Ít Ngọt — Sốt Không Nước Đường (Không Tiêu)'
                        : (calcData.isAll ? 'Tổng Hợp Định Lượng Bột Vỏ & Nhân Toàn Mẻ' : currentFilling.name)}
                </h2>
                <p className="recipe-hero-desc">
                    Tính chuẩn xác tỷ lệ bột vỏ nướng, bột vỏ dẻo và định lượng nhân cho từng cỡ khuôn 150g và 180g. Tự động liên kết theo đơn hàng thực tế của từng ngày giao.
                </p>
            </div>

            {/* BỘ LỌC THEO BILL VÀ NGÀY TRẢ HÀNG */}
            <section className="recipe-bill-filter-card">
                <div className="recipe-bill-filter-head">
                    <div className="recipe-bill-filter-title">
                        <span className="recipe-bill-badge">
                            <i className="fa-solid fa-receipt" /> ĐỒNG BỘ THEO ĐƠN HÀNG (BILL)
                        </span>
                        <h3>Lọc theo bill &amp; ngày trả hàng</h3>
                    </div>
                    {(dateFilter !== 'all' || slotFilter !== 'all' || orderFilter !== 'all') && (
                        <button
                            type="button"
                            className="btn-clear-recipe-filters"
                            onClick={() => {
                                setDateFilter('all');
                                setSlotFilter('all');
                                setOrderFilter('all');
                            }}
                        >
                            <i className="fa-solid fa-rotate-left" style={{ marginRight: '6px' }} />
                            Xem toàn bộ các đơn
                        </button>
                    )}
                </div>

                <div className="recipe-filter-grid">
                    {/* 1. Ngày trả hàng */}
                    <div className="recipe-filter-field">
                        <label>
                            <i className="fa-solid fa-calendar-day" style={{ color: '#142C54' }} />
                            Ngày trả bánh (Ngày giao):
                        </label>
                        <select
                            value={dateFilter}
                            onChange={(e) => setDateFilter(e.target.value)}
                        >
                            <option value="all">Tất cả các mẻ / ngày trả ({activeBills.length} đơn)</option>
                            {todayStr && (
                                <option value="today">
                                    Hôm nay - {formatDateShort(todayStr)} ({activeBills.filter(b => (b.delivery_date || '').startsWith(todayStr)).length} đơn)
                                </option>
                            )}
                            {tomorrowStr && (
                                <option value="tomorrow">
                                    Ngày mai - {formatDateShort(tomorrowStr)} ({activeBills.filter(b => (b.delivery_date || '').startsWith(tomorrowStr)).length} đơn)
                                </option>
                            )}
                            {availableDates.map(d => (
                                <option key={d} value={d}>
                                    Ngày {formatDateShort(d)} ({activeBills.filter(b => (b.delivery_date || '').startsWith(d)).length} đơn)
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 2. Khung giờ giao */}
                    <div className="recipe-filter-field">
                        <label>
                            <i className="fa-regular fa-clock" style={{ color: '#B8862F' }} />
                            Khung giờ giao:
                        </label>
                        <select
                            value={slotFilter}
                            onChange={(e) => setSlotFilter(e.target.value)}
                        >
                            <option value="all">Tất cả khung giờ</option>
                            <option value="09:00 – 12:00">09:00 – 12:00</option>
                            <option value="14:00 – 17:00">14:00 – 17:00</option>
                            <option value="18:00 – 21:00">18:00 – 21:00</option>
                        </select>
                    </div>

                    {/* 3. Lọc theo đơn hàng */}
                    <div className="recipe-filter-field">
                        <label>
                            <i className="fa-solid fa-file-invoice" style={{ color: '#7A6A5F' }} />
                            Lọc riêng đơn hàng:
                        </label>
                        <select
                            value={orderFilter}
                            onChange={(e) => setOrderFilter(e.target.value)}
                        >
                            <option value="all">Tất cả đơn hàng ({candidateOrders.length} đơn)</option>
                            {candidateOrders.map(b => (
                                <option key={b.id} value={b.orderCode}>
                                    #{b.orderCode} - {b.customerName || 'Khách'} ({countCakesInBill(b)} bánh)
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 4. Trạng thái thanh toán */}
                    <div className="recipe-filter-field">
                        <label>
                            <i className="fa-solid fa-filter" style={{ color: '#1E7E34' }} />
                            Trạng thái đơn:
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all_active">Tất cả đơn chưa giao (Mặc định)</option>
                            <option value="paid_only">Chỉ đơn đã thanh toán (chưa giao)</option>
                            <option value="pending_only">Chỉ đơn chờ chuyển khoản (chưa giao)</option>
                            <option value="delivered_only">Chỉ đơn đã giao hàng</option>
                            <option value="all_including_delivered">Tất cả đơn (gồm cả đã giao)</option>
                        </select>
                    </div>
                </div>

                {/* THANH CHỌN VỊ BÁNH (FILLING SELECTOR CHIPS) */}
                <div className="recipe-filling-selector-section">
                    <div className="recipe-filling-selector-title">
                        <span>
                            <i className="fa-solid fa-bowl-food" style={{ color: '#B8862F', marginRight: '6px' }} />
                            Chọn vị bánh cần tính công thức &amp; định lượng:
                        </span>
                        <span style={{ fontSize: '12px', color: '#7A6A5F' }}>
                            ({availableFillings.length} lựa chọn trong mẻ này)
                        </span>
                    </div>

                    <div className="recipe-filling-chips-row">
                        {availableFillings.map(item => {
                            const isSelected = (selectedFillingKey === item.key) || (selectedFillingKey === 'thapcam' && item.isThapCam);
                            return (
                                <button
                                    key={item.key}
                                    type="button"
                                    className={`recipe-filling-chip ${isSelected ? 'active' : ''} ${item.isAll ? 'chip-all' : ''} ${item.isThapCam ? 'chip-tc' : ''}`}
                                    onClick={() => {
                                        setSelectedFillingKey(item.key);
                                        showToast(`Đã chọn vị: ${item.name}`);
                                    }}
                                >
                                    <i className={`fa-solid ${item.icon}`} />
                                    <span className="chip-name">{item.shortName}</span>
                                    <span className="chip-count">{item.totalQty} bánh</span>
                                    {item.totalQty > 0 && (
                                        <span className="chip-sub">({item.totalNuong}N · {item.totalDeo}D)</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Thanh trạng thái vị bánh & nút đồng bộ nhanh */}
                <div className="recipe-bill-status-strip">
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '4px' }}>
                            <strong style={{ fontSize: '13.5px', color: '#241A16' }}>
                                <i className={`fa-solid ${currentFilling.icon}`} style={{ color: '#B8862F', marginRight: '4px' }} />
                                {currentFilling.name} ({formatSelectedDate(dateFilter)}):
                            </strong>
                            <span style={{ fontSize: '13px', fontWeight: 800, color: '#142C54', background: '#EEF4FB', padding: '2px 8px', borderRadius: '4px' }}>
                                {currentFilling.totalQty} chiếc
                            </span>
                            <span style={{ fontSize: '12px', color: '#6B5A4E' }}>
                                ({currentFilling.qty150} cái 150g · {currentFilling.qty180} cái 180g | {currentFilling.totalNuong} nướng · {currentFilling.totalDeo} dẻo)
                            </span>
                        </div>

                        {currentFilling.orders && currentFilling.orders.length > 0 ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#7A6A5F' }}>Đơn hàng có món này:</span>
                                {currentFilling.orders.slice(0, 10).map((ord, idx) => (
                                    <span
                                        key={idx}
                                        style={{
                                            fontSize: '11px',
                                            background: '#FFF',
                                            border: '1px solid #DCC9A4',
                                            padding: '1px 6px',
                                            borderRadius: '4px',
                                            color: '#241A16'
                                        }}
                                        title={`${ord.customerName || 'Khách'} · ${ord.qty} bánh (${ord.weight} ${ord.category})`}
                                    >
                                        #{ord.orderCode} ({ord.qty})
                                    </span>
                                ))}
                                {currentFilling.orders.length > 10 && (
                                    <span style={{ fontSize: '11px', color: '#7A6A5F' }}>+{currentFilling.orders.length - 10} đơn khác</span>
                                )}
                            </div>
                        ) : (
                            <div style={{ fontSize: '11.5px', color: '#976008', marginTop: '4px' }}>
                                <i>Mẻ/ngày này chưa có đơn hàng nào cho lựa chọn này.</i>
                            </div>
                        )}
                    </div>

                    <div className="recipe-bill-sync-actions">
                        <button
                            type="button"
                            className="recipe-btn-sync active"
                            onClick={() => {
                                setNuong150(currentFilling.nuong150);
                                setDeo150(currentFilling.deo150);
                                setNuong180(currentFilling.nuong180);
                                setDeo180(currentFilling.deo180);
                                setSyncSource('bill');
                                showToast(`Đã nạp đúng ${currentFilling.totalQty} bánh từ bill!`);
                            }}
                            title="Nạp chuẩn số lượng bánh theo đơn hàng của vị này"
                        >
                            <i className="fa-solid fa-arrows-rotate" />
                            Theo đơn: {currentFilling.totalQty} chiếc
                        </button>

                        <button
                            type="button"
                            className="recipe-btn-sync outline"
                            onClick={() => {
                                setNuong150(q => q + 1);
                                setDeo150(q => q + 1);
                                setSyncSource('custom');
                                showToast('Đã thêm 1 bánh dự phòng (nướng & dẻo)!');
                            }}
                            title="Tăng thêm 1 bánh dự phòng mỗi loại"
                        >
                            <i className="fa-solid fa-plus" />
                            Dự phòng (+1 chiếc)
                        </button>
                    </div>
                </div>
            </section>

            {/* Controls Bar */}
            <section className="recipe-controls">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', background: '#F4E9D5', padding: '8px 14px', borderRadius: '8px', marginBottom: '14px', fontSize: '12.5px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <i className="fa-solid fa-circle-info" style={{ color: '#142C54' }} />
                        <span>
                            Đang tính cho: <strong>{calcData.totalCakes} bánh</strong> ({calcData.totalNuong} nướng · {calcData.totalDeo} dẻo)
                            <span style={{ color: '#142C54', marginLeft: '6px', fontWeight: 700 }}>
                                [150g: {calcData.total150} chiếc | 180g: {calcData.total180} chiếc]
                            </span>
                            {syncSource === 'bill' && (
                                <span style={{ color: '#1E7E34', marginLeft: '6px', fontWeight: 700 }}>• Chuẩn theo bill</span>
                            )}
                            {syncSource === 'custom' && (
                                <span style={{ color: '#976008', marginLeft: '6px', fontWeight: 700 }}>• Đã chỉnh thêm bớt</span>
                            )}
                        </span>
                    </div>
                    <span style={{ color: '#7A6A5F', fontSize: '11.5px' }}>
                        Bấm [ - ] [ + ] để điều chỉnh thêm bớt số bánh theo ý muốn
                    </span>
                </div>

                {/* Chế độ tính & bộ lọc hiển thị vỏ */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', paddingBottom: '14px', borderBottom: '1px solid var(--line)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>
                            <i className="fa-solid fa-sliders" style={{ marginRight: '6px', color: 'var(--gold)' }} />
                            Chế độ tính mẻ:
                        </span>
                        <div className="recipe-btn-group" style={{ margin: 0 }}>
                            <button
                                type="button"
                                className={`recipe-btn-choice ${calcMode === 'mixed' ? 'active' : ''}`}
                                onClick={() => setCalcMode('mixed')}
                            >
                                <i className="fa-solid fa-layer-group" /> Mẻ gộp (Nướng &amp; Dẻo / 150g &amp; 180g)
                            </button>
                            <button
                                type="button"
                                className={`recipe-btn-choice ${calcMode === 'single' ? 'active' : ''}`}
                                onClick={() => setCalcMode('single')}
                            >
                                <i className="fa-solid fa-cube" /> Cỡ bánh đơn lẻ
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>
                            Xem loại vỏ:
                        </span>
                        <div className="recipe-btn-group" style={{ margin: 0 }}>
                            <button
                                type="button"
                                className={`recipe-btn-choice ${crustViewMode === 'both' ? 'active' : ''}`}
                                onClick={() => setCrustViewMode('both')}
                            >
                                <i className="fa-solid fa-cubes-stacked" /> Cả Nướng &amp; Dẻo
                            </button>
                            <button
                                type="button"
                                className={`recipe-btn-choice ${crustViewMode === 'nuong' ? 'active' : ''}`}
                                onClick={() => setCrustViewMode('nuong')}
                            >
                                <i className="fa-solid fa-fire-burner" /> Chỉ Vỏ Nướng
                            </button>
                            <button
                                type="button"
                                className={`recipe-btn-choice ${crustViewMode === 'deo' ? 'active' : ''}`}
                                onClick={() => setCrustViewMode('deo')}
                            >
                                <i className="fa-solid fa-moon" /> Chỉ Vỏ Dẻo
                            </button>
                        </div>
                    </div>
                </div>

                {calcMode === 'mixed' ? (
                    <div className="recipe-stepper-grid-4">
                        {/* 1. Nướng 150g */}
                        <div className="recipe-control-group">
                            <label className="recipe-control-label">
                                <span className="recipe-mini-badge nuong">NƯỚNG</span>
                                Bánh 150g (Vỏ 55g / Nhân 95g)
                            </label>
                            <div className="recipe-qty-box">
                                <div className="recipe-qty-stepper">
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        disabled={nuong150 <= 0}
                                        onClick={() => { setNuong150(q => Math.max(0, q - 1)); setSyncSource('custom'); }}
                                    >
                                        <i className="fa-solid fa-minus" />
                                    </button>
                                    <input
                                        type="number"
                                        min="0"
                                        max="500"
                                        value={nuong150}
                                        onChange={(e) => { setNuong150(Math.max(0, parseInt(e.target.value, 10) || 0)); setSyncSource('custom'); }}
                                        className="recipe-qty-input"
                                    />
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        onClick={() => { setNuong150(q => q + 1); setSyncSource('custom'); }}
                                    >
                                        <i className="fa-solid fa-plus" />
                                    </button>
                                </div>
                                <span style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 600 }}>chiếc</span>
                            </div>
                        </div>

                        {/* 2. Dẻo 150g */}
                        <div className="recipe-control-group">
                            <label className="recipe-control-label">
                                <span className="recipe-mini-badge deo">DẺO</span>
                                Bánh 150g (Vỏ 75g / Nhân 75g)
                            </label>
                            <div className="recipe-qty-box">
                                <div className="recipe-qty-stepper">
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        disabled={deo150 <= 0}
                                        onClick={() => { setDeo150(q => Math.max(0, q - 1)); setSyncSource('custom'); }}
                                    >
                                        <i className="fa-solid fa-minus" />
                                    </button>
                                    <input
                                        type="number"
                                        min="0"
                                        max="500"
                                        value={deo150}
                                        onChange={(e) => { setDeo150(Math.max(0, parseInt(e.target.value, 10) || 0)); setSyncSource('custom'); }}
                                        className="recipe-qty-input"
                                    />
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        onClick={() => { setDeo150(q => q + 1); setSyncSource('custom'); }}
                                    >
                                        <i className="fa-solid fa-plus" />
                                    </button>
                                </div>
                                <span style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 600 }}>chiếc</span>
                            </div>
                        </div>

                        {/* 3. Nướng 180g */}
                        <div className="recipe-control-group">
                            <label className="recipe-control-label">
                                <span className="recipe-mini-badge nuong">NƯỚNG</span>
                                Bánh 180g (Vỏ 65g / Nhân 115g)
                            </label>
                            <div className="recipe-qty-box">
                                <div className="recipe-qty-stepper">
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        disabled={nuong180 <= 0}
                                        onClick={() => { setNuong180(q => Math.max(0, q - 1)); setSyncSource('custom'); }}
                                    >
                                        <i className="fa-solid fa-minus" />
                                    </button>
                                    <input
                                        type="number"
                                        min="0"
                                        max="500"
                                        value={nuong180}
                                        onChange={(e) => { setNuong180(Math.max(0, parseInt(e.target.value, 10) || 0)); setSyncSource('custom'); }}
                                        className="recipe-qty-input"
                                    />
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        onClick={() => { setNuong180(q => q + 1); setSyncSource('custom'); }}
                                    >
                                        <i className="fa-solid fa-plus" />
                                    </button>
                                </div>
                                <span style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 600 }}>chiếc</span>
                            </div>
                        </div>

                        {/* 4. Dẻo 180g */}
                        <div className="recipe-control-group">
                            <label className="recipe-control-label">
                                <span className="recipe-mini-badge deo">DẺO</span>
                                Bánh 180g (Vỏ 90g / Nhân 90g)
                            </label>
                            <div className="recipe-qty-box">
                                <div className="recipe-qty-stepper">
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        disabled={deo180 <= 0}
                                        onClick={() => { setDeo180(q => Math.max(0, q - 1)); setSyncSource('custom'); }}
                                    >
                                        <i className="fa-solid fa-minus" />
                                    </button>
                                    <input
                                        type="number"
                                        min="0"
                                        max="500"
                                        value={deo180}
                                        onChange={(e) => { setDeo180(Math.max(0, parseInt(e.target.value, 10) || 0)); setSyncSource('custom'); }}
                                        className="recipe-qty-input"
                                    />
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        onClick={() => { setDeo180(q => q + 1); setSyncSource('custom'); }}
                                    >
                                        <i className="fa-solid fa-plus" />
                                    </button>
                                </div>
                                <span style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 600 }}>chiếc</span>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="recipe-control-grid">
                        <div className="recipe-control-group">
                            <label className="recipe-control-label">
                                <i className="fa-solid fa-circle-notch" /> Chọn kích cỡ khuôn
                            </label>
                            <div className="recipe-btn-group">
                                <button
                                    type="button"
                                    className={`recipe-btn-choice ${singleSize === 150 ? 'active' : ''}`}
                                    onClick={() => setSingleSize(150)}
                                >
                                    Khuôn 150g
                                </button>
                                <button
                                    type="button"
                                    className={`recipe-btn-choice ${singleSize === 180 ? 'active' : ''}`}
                                    onClick={() => setSingleSize(180)}
                                >
                                    Khuôn 180g
                                </button>
                            </div>
                        </div>

                        <div className="recipe-control-group">
                            <label className="recipe-control-label">
                                <i className="fa-solid fa-fire" /> Loại vỏ bánh
                            </label>
                            <div className="recipe-btn-group">
                                <button
                                    type="button"
                                    className={`recipe-btn-choice ${singleType === 'nuong' ? 'active' : ''}`}
                                    onClick={() => setSingleType('nuong')}
                                >
                                    Vỏ nướng
                                </button>
                                <button
                                    type="button"
                                    className={`recipe-btn-choice ${singleType === 'deo' ? 'active' : ''}`}
                                    onClick={() => setSingleType('deo')}
                                >
                                    Vỏ dẻo
                                </button>
                            </div>
                        </div>

                        <div className="recipe-control-group">
                            <label className="recipe-control-label">
                                <i className="fa-solid fa-scale-balanced" /> Số lượng bánh
                            </label>
                            <div className="recipe-qty-box">
                                <div className="recipe-qty-stepper">
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        disabled={singleQty <= 1}
                                        onClick={() => setSingleQty(q => Math.max(1, q - 1))}
                                    >
                                        <i className="fa-solid fa-minus" />
                                    </button>
                                    <input
                                        type="number"
                                        min="1"
                                        max="500"
                                        value={singleQty}
                                        onChange={(e) => setSingleQty(Math.max(1, parseInt(e.target.value, 10) || 1))}
                                        className="recipe-qty-input"
                                    />
                                    <button
                                        type="button"
                                        className="recipe-qty-btn"
                                        onClick={() => setSingleQty(q => q + 1)}
                                    >
                                        <i className="fa-solid fa-plus" />
                                    </button>
                                </div>
                                <span style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 600 }}>chiếc</span>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* DASHBOARD TỔNG QUAN NGUYÊN LIỆU MẺ NÀY */}
            <div className="recipe-summary-bar">
                <div className="recipe-summary-header">
                    <div className="recipe-summary-title">
                        <i className="fa-solid fa-calculator" />
                        Tổng quan mẻ: {calcData.totalCakes} bánh ({calcData.totalNuong} nướng · {calcData.totalDeo} dẻo)
                    </div>
                    <div className="recipe-actions">
                        <button type="button" className="recipe-action-btn" onClick={handleCopy}>
                            <i className="fa-solid fa-copy" /> Sao chép công thức
                        </button>
                        <button type="button" className="recipe-action-btn primary" onClick={() => window.print()}>
                            <i className="fa-solid fa-print" /> In phiếu cân
                        </button>
                    </div>
                </div>

                <div className="recipe-summary-grid">
                    {/* Card 1: Bột vỏ nướng */}
                    <div className="recipe-stat-box">
                        <div className="recipe-stat-box-title">
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <i className="fa-solid fa-fire-burner" style={{ color: '#142C54' }} />
                                Bột vỏ bánh nướng
                            </span>
                            <span className="recipe-stat-box-badge navy">{calcData.totalNuong} chiếc</span>
                        </div>
                        <div className="recipe-stat-box-body">
                            <div className="recipe-stat-metric">
                                <span>Cỡ 150g ({calcData.nuong150}c):</span>
                                <strong>{formatRoundGram(calcData.crustNuongTotal.sum150)}</strong>
                            </div>
                            <div className="recipe-stat-metric">
                                <span>Cỡ 180g ({calcData.nuong180}c):</span>
                                <strong>{formatRoundGram(calcData.crustNuongTotal.sum180)}</strong>
                            </div>
                            <div className="recipe-stat-metric brand">
                                <span>TỔNG VỎ NƯỚNG:</span>
                                <strong>{formatRoundGram(calcData.crustNuongTotal.totalWeight)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* Card 2: Bột vỏ dẻo */}
                    <div className="recipe-stat-box">
                        <div className="recipe-stat-box-title">
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <i className="fa-solid fa-moon" style={{ color: '#1E7E34' }} />
                                Bột vỏ bánh dẻo
                            </span>
                            <span className="recipe-stat-box-badge green">{calcData.totalDeo} chiếc</span>
                        </div>
                        <div className="recipe-stat-box-body">
                            <div className="recipe-stat-metric">
                                <span>Cỡ 150g ({calcData.deo150}c):</span>
                                <strong>{formatRoundGram(calcData.crustDeoTotal.sum150)}</strong>
                            </div>
                            <div className="recipe-stat-metric">
                                <span>Cỡ 180g ({calcData.deo180}c):</span>
                                <strong>{formatRoundGram(calcData.crustDeoTotal.sum180)}</strong>
                            </div>
                            <div className="recipe-stat-metric brand">
                                <span>TỔNG VỎ DẺO:</span>
                                <strong>{formatRoundGram(calcData.crustDeoTotal.totalWeight)}</strong>
                            </div>
                        </div>
                    </div>

                    {/* Card 3: Nhân bánh */}
                    <div className="recipe-stat-box highlight">
                        <div className="recipe-stat-box-title">
                            <span style={{ color: 'var(--red-deep)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <i className="fa-solid fa-bowl-rice" />
                                {calcData.isNF ? 'BÁNH KHÔNG NHÂN' : 'NHÂN BÁNH CẦN DÙNG'}
                            </span>
                            <span className="recipe-stat-box-badge" style={{ background: 'var(--red)', color: '#FFF' }}>
                                {calcData.isNF ? '0g nhân' : `${calcData.totalCakes} viên`}
                            </span>
                        </div>
                        <div className="recipe-stat-box-body">
                            <div className="recipe-stat-metric brand">
                                <span>Nhân bánh 150g:</span>
                                <strong>{formatRoundGram(calcData.fillingGram150)}</strong>
                            </div>
                            <div className="recipe-stat-metric brand">
                                <span>Nhân bánh 180g:</span>
                                <strong>{formatRoundGram(calcData.fillingGram180)}</strong>
                            </div>
                            <div className="recipe-stat-metric brand" style={{ borderTop: '1px solid rgba(193, 39, 45, 0.25)', paddingTop: '6px' }}>
                                <span>TỔNG NHÂN TOÀN MẺ:</span>
                                <strong style={{ color: 'var(--red)' }}>
                                    {calcData.isNF ? '0g (Thuần vỏ)' : formatRoundGram(calcData.totalFillingWeight)}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* PHẦN 1: BỘT VỎ BÁNH NƯỚNG (NẾU CÓ BÁNH NƯỚNG HOẶC CHỌN XEM) */}
            {(crustViewMode === 'both' || crustViewMode === 'nuong') && (
                <section className="recipe-section">
                    <div className="recipe-section-head">
                        <h3 className="recipe-section-title">
                            <i className="fa-solid fa-fire-burner" style={{ color: '#142C54' }} />
                            1. Bột Vỏ Bánh Nướng ({calcData.totalNuong} bánh nướng — Tổng bột vỏ: {formatRoundGram(calcData.crustNuongTotal.totalWeight)})
                        </h3>
                        <span className="recipe-section-badge">
                            {calcData.isNF ? 'Bánh không nhân: 150g / 180g vỏ' : 'Khuôn 150g dùng 55g vỏ · Khuôn 180g dùng 65g vỏ'}
                        </span>
                    </div>

                    <div className="recipe-card">
                        <div className="recipe-table-wrap">
                            <table className="recipe-table">
                                <thead>
                                    <tr>
                                        <th className="col-name">Nguyên liệu vỏ nướng</th>
                                        <th className="tar">1 vỏ 150g<span className="th-sub">{calcData.unitCrustNuong150}g vỏ</span></th>
                                        <th className="tar">1 vỏ 180g<span className="th-sub">{calcData.unitCrustNuong180}g vỏ</span></th>
                                        <th className="tar col-150">Tổng {calcData.nuong150} vỏ 150g<span className="th-sub">Tính riêng</span></th>
                                        <th className="tar col-180">Tổng {calcData.nuong180} vỏ 180g<span className="th-sub">Tính riêng</span></th>
                                        <th className="tar col-total">TỔNG VỎ NƯỚNG CẢ MẺ<span className="th-sub">Toàn bộ</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        { key: 'botMi', name: 'Bột mì (số 8 hoặc 11 - 56%)', u150: calcData.single150_crustNuong.botMi, u180: calcData.single180_crustNuong.botMi },
                                        { key: 'nuocDuong', name: 'Nước đường bánh nướng (34%)', u150: calcData.single150_crustNuong.nuocDuong, u180: calcData.single180_crustNuong.nuocDuong },
                                        { key: 'dauAn', name: 'Dầu ăn tinh luyện (6%)', u150: calcData.single150_crustNuong.dauAn, u180: calcData.single180_crustNuong.dauAn },
                                        { key: 'boDauPhong', name: 'Bơ đậu phộng mịn / lòng đỏ (4%)', u150: calcData.single150_crustNuong.boDauPhong, u180: calcData.single180_crustNuong.boDauPhong },
                                    ].map((item) => (
                                        <tr key={item.key}>
                                            <td><strong>{item.name}</strong></td>
                                            <td className="tar">{formatGram(item.u150)}</td>
                                            <td className="tar">{formatGram(item.u180)}</td>
                                            <td className="tar col-150"><span className="recipe-gram-tag tint-150">{formatRoundGram(calcData.crustNuongTotal.details150[item.key])}</span></td>
                                            <td className="tar col-180"><span className="recipe-gram-tag tint-180">{formatRoundGram(calcData.crustNuongTotal.details180[item.key])}</span></td>
                                            <td className="tar col-total"><span className="recipe-gram-tag brand">{formatRoundGram(calcData.crustNuongTotal.grandDetails[item.key])}</span></td>
                                        </tr>
                                    ))}
                                    <tr className="total-row">
                                        <td><strong>TỔNG BỘT VỎ BÁNH NƯỚNG</strong></td>
                                        <td className="tar"><strong>{calcData.unitCrustNuong150}g</strong></td>
                                        <td className="tar"><strong>{calcData.unitCrustNuong180}g</strong></td>
                                        <td className="tar col-150"><strong>{formatRoundGram(calcData.crustNuongTotal.sum150)}</strong></td>
                                        <td className="tar col-180"><strong>{formatRoundGram(calcData.crustNuongTotal.sum180)}</strong></td>
                                        <td className="tar col-total"><strong>{formatRoundGram(calcData.crustNuongTotal.totalWeight)}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {/* PHẦN 2: BỘT VỎ BÁNH DẺO (NẾU CÓ BÁNH DẺO HOẶC CHỌN XEM) */}
            {(crustViewMode === 'both' || crustViewMode === 'deo') && (
                <section className="recipe-section">
                    <div className="recipe-section-head">
                        <h3 className="recipe-section-title">
                            <i className="fa-solid fa-moon" style={{ color: '#1E7E34' }} />
                            2. Bột Vỏ Bánh Dẻo ({calcData.totalDeo} bánh dẻo — Tổng bột vỏ: {formatRoundGram(calcData.crustDeoTotal.totalWeight)})
                        </h3>
                        <span className="recipe-section-badge">
                            {calcData.isNF ? 'Bánh không nhân: 150g / 180g vỏ' : 'Khuôn 150g dùng 75g vỏ (1:1) · Khuôn 180g dùng 90g vỏ (1:1)'}
                        </span>
                    </div>

                    <div className="recipe-card">
                        <div className="recipe-table-wrap">
                            <table className="recipe-table">
                                <thead>
                                    <tr>
                                        <th className="col-name">Nguyên liệu vỏ dẻo</th>
                                        <th className="tar">1 vỏ 150g<span className="th-sub">{calcData.unitCrustDeo150}g vỏ</span></th>
                                        <th className="tar">1 vỏ 180g<span className="th-sub">{calcData.unitCrustDeo180}g vỏ</span></th>
                                        <th className="tar col-150">Tổng {calcData.deo150} vỏ 150g<span className="th-sub">Tính riêng</span></th>
                                        <th className="tar col-180">Tổng {calcData.deo180} vỏ 180g<span className="th-sub">Tính riêng</span></th>
                                        <th className="tar col-total">TỔNG VỎ DẺO CẢ MẺ<span className="th-sub">Toàn bộ</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[
                                        { key: 'botBanhDeo', name: 'Bột bánh dẻo (nếp chín hảo hạng - 33%)', u150: calcData.single150_crustDeo.botBanhDeo, u180: calcData.single180_crustDeo.botBanhDeo },
                                        { key: 'nuocDuong', name: 'Nước đường bánh dẻo (65%)', u150: calcData.single150_crustDeo.nuocDuong, u180: calcData.single180_crustDeo.nuocDuong },
                                        { key: 'dauAn', name: 'Dầu ăn thực vật (2%)', u150: calcData.single150_crustDeo.dauAn, u180: calcData.single180_crustDeo.dauAn },
                                    ].map((item) => (
                                        <tr key={item.key}>
                                            <td><strong>{item.name}</strong></td>
                                            <td className="tar">{formatGram(item.u150)}</td>
                                            <td className="tar">{formatGram(item.u180)}</td>
                                            <td className="tar col-150"><span className="recipe-gram-tag tint-150">{formatRoundGram(calcData.crustDeoTotal.details150[item.key])}</span></td>
                                            <td className="tar col-180"><span className="recipe-gram-tag tint-180">{formatRoundGram(calcData.crustDeoTotal.details180[item.key])}</span></td>
                                            <td className="tar col-total"><span className="recipe-gram-tag brand">{formatRoundGram(calcData.crustDeoTotal.grandDetails[item.key])}</span></td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td><strong>Tinh dầu hoa bưởi</strong></td>
                                        <td className="tar">2 giọt</td>
                                        <td className="tar">3 giọt</td>
                                        <td className="tar col-150"><span className="recipe-gram-tag tint-150">{calcData.crustDeoTotal.details150.hoaBuoi} giọt</span></td>
                                        <td className="tar col-180"><span className="recipe-gram-tag tint-180">{calcData.crustDeoTotal.details180.hoaBuoi} giọt</span></td>
                                        <td className="tar col-total"><span className="recipe-gram-tag brand">{calcData.crustDeoTotal.grandDetails.hoaBuoi} giọt</span></td>
                                    </tr>
                                    <tr className="total-row">
                                        <td><strong>TỔNG BỘT VỎ BÁNH DẺO</strong></td>
                                        <td className="tar"><strong>{calcData.unitCrustDeo150}g</strong></td>
                                        <td className="tar"><strong>{calcData.unitCrustDeo180}g</strong></td>
                                        <td className="tar col-150"><strong>{formatRoundGram(calcData.crustDeoTotal.sum150)}</strong></td>
                                        <td className="tar col-180"><strong>{formatRoundGram(calcData.crustDeoTotal.sum180)}</strong></td>
                                        <td className="tar col-total"><strong>{formatRoundGram(calcData.crustDeoTotal.totalWeight)}</strong></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            )}

            {/* PHẦN 3: ĐỊNH LƯỢNG NHÂN BÁNH */}
            {calcData.isTC ? (
                /* THẬP CẨM: HIỂN THỊ CÔNG THỨC ĐỘC QUYỀN 11 HẠT MỨT & SỐT TRỘN */
                <>
                    <section className="recipe-section">
                        <div className="recipe-section-head">
                            <h3 className="recipe-section-title">
                                <i className="fa-solid fa-seedling" />
                                3. Nguyên Liệu Hạt, Mứt &amp; Kết Dính Thập Cẩm ({formatRoundGram(calcData.tcGrandTotal.filling)} nhân)
                            </h3>
                            <span className="recipe-section-badge highlight">
                                Công thức hạt ít ngọt chuẩn tiệm (Không dùng tiêu)
                            </span>
                        </div>

                        <div className="recipe-card">
                            <div className="recipe-table-wrap">
                                <table className="recipe-table">
                                    <thead>
                                        <tr>
                                            <th className="col-name">Nguyên liệu nhân thập cẩm</th>
                                            <th className="tar">1 chiếc 150g<span className="th-sub">95g nhân</span></th>
                                            <th className="tar">1 chiếc 180g<span className="th-sub">115g nhân</span></th>
                                            <th className="tar col-150">Tổng {calcData.total150} bánh 150g<span className="th-sub">Tính riêng</span></th>
                                            <th className="tar col-180">Tổng {calcData.total180} bánh 180g<span className="th-sub">Tính riêng</span></th>
                                            <th className="tar col-total">TỔNG CẢ MẺ ({calcData.totalCakes} bánh)<span className="th-sub">Toàn bộ</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { key: 'mutSen', name: 'Mứt sen (loại ít ngọt / sen sấy giòn)', u150: calcData.single150_tc_dry.mutSen, u180: calcData.single180_tc_dry.mutSen },
                                            { key: 'mutBi', name: 'Mứt bí đao (đã chần xả bớt đường)', u150: calcData.single150_tc_dry.mutBi, u180: calcData.single180_tc_dry.mutBi },
                                            { key: 'mutGung', name: 'Mứt gừng đỏ', u150: calcData.single150_tc_dry.mutGung, u180: calcData.single180_tc_dry.mutGung },
                                            { key: 'hatDieu', name: 'Hạt điều rang bùi', u150: calcData.single150_tc_dry.hatDieu, u180: calcData.single180_tc_dry.hatDieu },
                                            { key: 'lacRang', name: 'Lạc rang (Đậu phộng)', u150: calcData.single150_tc_dry.lacRang, u180: calcData.single180_tc_dry.lacRang },
                                            { key: 'hatBiXanh', name: 'Hạt bí xanh tách vỏ', u150: calcData.single150_tc_dry.hatBiXanh, u180: calcData.single180_tc_dry.hatBiXanh },
                                            { key: 'hatDua', name: 'Hạt dưa tách nõn', u150: calcData.single150_tc_dry.hatDua, u180: calcData.single180_tc_dry.hatDua },
                                            { key: 'vungTrang', name: 'Vừng trắng rang (Mè trắng)', u150: calcData.single150_tc_dry.vungTrang, u180: calcData.single180_tc_dry.vungTrang },
                                            { key: 'laChanh', name: 'Lá chanh tươi thái chỉ tơ', u150: calcData.single150_tc_dry.laChanh, u180: calcData.single180_tc_dry.laChanh },
                                        ].map((item) => (
                                            <tr key={item.key}>
                                                <td><strong>{item.name}</strong></td>
                                                <td className="tar">{formatGram(item.u150)}</td>
                                                <td className="tar">{formatGram(item.u180)}</td>
                                                <td className="tar col-150"><span className="recipe-gram-tag tint-150">{formatRoundGram(calcData.tcTotal150.dry[item.key])}</span></td>
                                                <td className="tar col-180"><span className="recipe-gram-tag tint-180">{formatRoundGram(calcData.tcTotal180.dry[item.key])}</span></td>
                                                <td className="tar col-total"><span className="recipe-gram-tag brand">{formatRoundGram(calcData.tcGrandTotal.dry[item.key])}</span></td>
                                            </tr>
                                        ))}

                                        <tr className="highlight-row">
                                            <td><strong style={{ color: 'var(--gold)' }}>Bột bánh dẻo (Bột kết dính)</strong></td>
                                            <td className="tar">{formatGram(calcData.single150_tc_dry.botBanhDeo)}</td>
                                            <td className="tar">{formatGram(calcData.single180_tc_dry.botBanhDeo)}</td>
                                            <td className="tar col-150"><span className="recipe-gram-tag tint-150">{formatRoundGram(calcData.tcTotal150.dry.botBanhDeo)}</span></td>
                                            <td className="tar col-180"><span className="recipe-gram-tag tint-180">{formatRoundGram(calcData.tcTotal180.dry.botBanhDeo)}</span></td>
                                            <td className="tar col-total"><span className="recipe-gram-tag brand">{formatRoundGram(calcData.tcGrandTotal.dry.botBanhDeo)}</span></td>
                                        </tr>

                                        <tr className="highlight-row">
                                            <td><strong style={{ color: 'var(--red)' }}>Nước sốt trộn không đường (Mục 4 bên dưới)</strong></td>
                                            <td className="tar">{formatGram(calcData.single150_tc_dry.sotTron)}</td>
                                            <td className="tar">{formatGram(calcData.single180_tc_dry.sotTron)}</td>
                                            <td className="tar col-150"><span className="recipe-gram-tag tint-150">{formatRoundGram(calcData.tcTotal150.sauce.totalSauce)}</span></td>
                                            <td className="tar col-180"><span className="recipe-gram-tag tint-180">{formatRoundGram(calcData.tcTotal180.sauce.totalSauce)}</span></td>
                                            <td className="tar col-total"><span className="recipe-gram-tag brand">{formatRoundGram(calcData.tcGrandTotal.sauce.totalSauce)}</span></td>
                                        </tr>

                                        <tr className="total-row">
                                            <td><strong>TỔNG VIÊN NHÂN HOÀN CHỈNH</strong></td>
                                            <td className="tar"><strong>95.0g</strong></td>
                                            <td className="tar"><strong>115.0g</strong></td>
                                            <td className="tar col-150"><strong>{formatRoundGram(calcData.total150 * 95)}</strong></td>
                                            <td className="tar col-180"><strong>{formatRoundGram(calcData.total180 * 115)}</strong></td>
                                            <td className="tar col-total"><strong>{formatRoundGram(calcData.tcGrandTotal.filling)}</strong></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="recipe-guide-box" style={{ background: '#FFFBEB', borderTop: '1px solid #FDE68A' }}>
                                <div className="recipe-guide-title" style={{ color: '#92400E', fontSize: '14px' }}>
                                    <i className="fa-solid fa-triangle-exclamation" style={{ color: '#D97706' }} /> Lưu ý kỹ thuật nhân ít ngọt (Không dùng tiêu):
                                </div>
                                <ul className="recipe-guide-list" style={{ color: '#78350F' }}>
                                    <li><strong>Xả áo đường mứt bí (Bắt buộc):</strong> Mứt bí thái hạt lựu đem chần nước sôi khoảng 30 giây (hoặc xả nhanh dưới vòi nước ấm), thấm thật ráo kiệt trước khi trộn để giảm hơn 40% lượng đường gắt.</li>
                                    <li><strong>Không thay toàn bộ nước đường bằng nước:</strong> Giữ tỷ lệ sốt và dầu mè, dầu hào chuẩn giúp nhân bóng dẻo, ngậy thơm và đứng form.</li>
                                    <li><strong>Chỉ cho 80–85% lượng sốt trước:</strong> Với mẻ này (cần {formatRoundGram(calcData.tcGrandTotal.sauce.totalSauce)} sốt), rưới khoảng {Math.round(calcData.tcGrandTotal.sauce.totalSauce * 0.82)}g sốt trước, trộn đều kiểm tra độ khô của mẻ hạt rồi mới thêm nốt phần sốt còn lại.</li>
                                    <li><strong>Bột bánh dẻo rắc từng ít một ({formatRoundGram(calcData.tcGrandTotal.dry.botBanhDeo)}):</strong> Rắc từ từ, vừa rắc vừa bóp nhẹ tay. Dừng lại ngay khi nắm nhân vo được thành viên tròn chắc nịch, <strong>không bở rời và không tứa dầu</strong>.</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section className="recipe-section">
                        <div className="recipe-section-head">
                            <h3 className="recipe-section-title">
                                <i className="fa-solid fa-flask" />
                                4. Nước Sốt Trộn Không Nước Đường ({formatRoundGram(calcData.tcGrandTotal.sauce.totalSauce)})
                            </h3>
                            <span className="recipe-section-badge">
                                Tỷ lệ chuẩn: Cứ mỗi 150g sốt (Không nước đường · Không tiêu)
                            </span>
                        </div>

                        <div className="recipe-card">
                            <div className="recipe-table-wrap">
                                <table className="recipe-table">
                                    <thead>
                                        <tr>
                                            <th className="col-name">Nguyên liệu sốt</th>
                                            <th className="tar">1 bánh 150g<span className="th-sub">15g sốt</span></th>
                                            <th className="tar">1 bánh 180g<span className="th-sub">18.1g sốt</span></th>
                                            <th className="tar col-150">Tổng {calcData.total150} bánh 150g<span className="th-sub">Tính riêng</span></th>
                                            <th className="tar col-180">Tổng {calcData.total180} bánh 180g<span className="th-sub">Tính riêng</span></th>
                                            <th className="tar col-total">TỔNG SỐT CẢ MẺ<span className="th-sub">Toàn bộ</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[
                                            { key: 'nuocLoc', name: 'Nước lọc đun sôi để nguội', u150: calcData.single150_tc_sauce.nuocLoc, u180: calcData.single180_tc_sauce.nuocLoc },
                                            { key: 'dauAn', name: 'Dầu ăn tinh luyện', u150: calcData.single150_tc_sauce.dauAn, u180: calcData.single180_tc_sauce.dauAn },
                                            { key: 'dauMe', name: 'Dầu mè thơm', u150: calcData.single150_tc_sauce.dauMe, u180: calcData.single180_tc_sauce.dauMe },
                                            { key: 'ruouMQL', name: 'Rượu Mai Quế Lộ (loại thơm, không đường)', u150: calcData.single150_tc_sauce.ruouMQL, u180: calcData.single180_tc_sauce.ruouMQL },
                                            { key: 'nuocTuong', name: 'Nước tương (Xì dầu)', u150: calcData.single150_tc_sauce.nuocTuong, u180: calcData.single180_tc_sauce.nuocTuong },
                                            { key: 'dauHao', name: 'Dầu hào hảo hạng', u150: calcData.single150_tc_sauce.dauHao, u180: calcData.single180_tc_sauce.dauHao },
                                            { key: 'muoiTinh', name: 'Muối tinh (Bí quyết ghìm ngọt & dậy vị bùi)', u150: calcData.single150_tc_sauce.muoiTinh, u180: calcData.single180_tc_sauce.muoiTinh },
                                        ].map((item) => (
                                            <tr key={item.key}>
                                                <td><strong>{item.name}</strong></td>
                                                <td className="tar">{formatGram(item.u150)}</td>
                                                <td className="tar">{formatGram(item.u180)}</td>
                                                <td className="tar col-150"><span className="recipe-gram-tag tint-150">{formatRoundGram(calcData.tcTotal150.sauce[item.key])}</span></td>
                                                <td className="tar col-180"><span className="recipe-gram-tag tint-180">{formatRoundGram(calcData.tcTotal180.sauce[item.key])}</span></td>
                                                <td className="tar col-total"><span className="recipe-gram-tag brand">{formatRoundGram(calcData.tcGrandTotal.sauce[item.key])}</span></td>
                                            </tr>
                                        ))}

                                        <tr className="total-row">
                                            <td><strong>TỔNG NƯỚC SỐT TRỘN</strong></td>
                                            <td className="tar"><strong>{formatGram(calcData.single150_tc_sauce.totalSauce)}</strong></td>
                                            <td className="tar"><strong>{formatGram(calcData.single180_tc_sauce.totalSauce)}</strong></td>
                                            <td className="tar col-150"><strong>{formatRoundGram(calcData.tcTotal150.sauce.totalSauce)}</strong></td>
                                            <td className="tar col-180"><strong>{formatRoundGram(calcData.tcTotal180.sauce.totalSauce)}</strong></td>
                                            <td className="tar col-total"><strong>{formatRoundGram(calcData.tcGrandTotal.sauce.totalSauce)}</strong></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>
                </>
            ) : calcData.isAll ? (
                /* TOÀN MẺ: BẢNG TỔNG HỢP CÁC VỊ NHÂN TRONG MẺ */
                <section className="recipe-section">
                    <div className="recipe-section-head">
                        <h3 className="recipe-section-title">
                            <i className="fa-solid fa-list-check" />
                            3. Tổng Hợp Nhân Từng Vị Cần Làm Trong Mẻ ({availableFillings.filter(f => !f.isAll).length} vị bánh)
                        </h3>
                        <span className="recipe-section-badge">
                            Tổng cộng: {formatRoundGram(calcData.totalFillingWeight)} nhân (~{(calcData.totalFillingWeight / 1000).toFixed(2)} kg)
                        </span>
                    </div>

                    <div className="recipe-card">
                        <div className="recipe-table-wrap">
                            <table className="recipe-table">
                                <thead>
                                    <tr>
                                        <th className="col-name">Vị nhân bánh</th>
                                        <th className="tar">Bánh 150g<span className="th-sub">Nướng · Dẻo</span></th>
                                        <th className="tar">Bánh 180g<span className="th-sub">Nướng · Dẻo</span></th>
                                        <th className="tar">Tổng số bánh<span className="th-sub">Chiếc</span></th>
                                        <th className="tar">Trứng muối<span className="th-sub">Quả</span></th>
                                        <th className="tar col-total">Khối lượng nhân<span className="th-sub">Kg chuẩn</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {availableFillings.filter(f => !f.isAll).map(f => {
                                        const fWeight150 = f.isNoFilling ? 0 : (f.nuong150 * 95 + f.deo150 * 75);
                                        const fWeight180 = f.isNoFilling ? 0 : (f.nuong180 * 115 + f.deo180 * 90);
                                        const fTotalG = fWeight150 + fWeight180;
                                        const fTotalKg = (fTotalG / 1000).toFixed(2);

                                        return (
                                            <tr key={f.key}>
                                                <td>
                                                    <strong>
                                                        <i className={`fa-solid ${f.icon}`} style={{ color: '#B8862F', marginRight: '6px' }} />
                                                        {f.name}
                                                    </strong>
                                                </td>
                                                <td className="tar">
                                                    {f.qty150} chiếc ({f.nuong150}N · {f.deo150}D)
                                                </td>
                                                <td className="tar">
                                                    {f.qty180} chiếc ({f.nuong180}N · {f.deo180}D)
                                                </td>
                                                <td className="tar">
                                                    <span style={{ fontWeight: 800, color: '#142C54' }}>{f.totalQty}</span>
                                                </td>
                                                <td className="tar">
                                                    {f.eggQty > 0 ? (
                                                        <span className="recipe-gram-tag gold">{f.eggQty} quả</span>
                                                    ) : (
                                                        <span style={{ color: '#999' }}>—</span>
                                                    )}
                                                </td>
                                                <td className="tar col-total">
                                                    <span className="recipe-gram-tag brand">
                                                        {f.isNoFilling ? '0 kg (Thuần vỏ)' : `${fTotalKg} kg (${formatRoundGram(fTotalG)})`}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </section>
            ) : (
                /* VỊ NHÂN CỤ THỂ (ĐẬU XANH, TRÀ XANH, CỐM DỪA, THUẦN VỎ, ...) */
                <section className="recipe-section">
                    <div className="recipe-section-head">
                        <h3 className="recipe-section-title">
                            <i className={`fa-solid ${currentFilling.icon}`} />
                            3. Định Lượng {currentFilling.name} ({formatRoundGram(calcData.totalFillingWeight)} nhân)
                        </h3>
                        <span className="recipe-section-badge">
                            {calcData.isNF ? 'Bánh không nhân (Thuần vỏ): 0g nhân' : 'Định lượng nhân nhuyễn sên sẵn chuẩn xưởng'}
                        </span>
                    </div>

                    <div className="recipe-card">
                        {calcData.isNF ? (
                            <div style={{ padding: '24px', textAlign: 'center', background: '#FFFDF9' }}>
                                <div style={{ fontSize: '38px', color: '#1E7E34', marginBottom: '10px' }}>
                                    <i className="fa-solid fa-circle-check" />
                                </div>
                                <h4 style={{ fontSize: '18px', color: '#142C54', margin: '0 0 8px' }}>
                                    Bánh thuần vỏ không dùng nhân (0 kg nhân)
                                </h4>
                                <p style={{ fontSize: '13.5px', color: '#6B5A4E', maxWidth: '600px', margin: '0 auto', lineHeight: 1.6 }}>
                                    Toàn bộ viên bánh được làm từ bột vỏ bánh dẻo hoặc nướng. Mẻ này cần tổng cộng <strong>{formatRoundGram(calcData.totalDoughWeight)} bột vỏ</strong> (Chi tiết từng thành phần bột, nước đường, dầu ăn đã tính đủ tại Mục 1 &amp; 2 ở trên).
                                </p>
                            </div>
                        ) : (
                            <div className="recipe-table-wrap">
                                <table className="recipe-table">
                                    <thead>
                                        <tr>
                                            <th className="col-name">Phần nhân chuẩn bị</th>
                                            <th className="tar">1 chiếc 150g<span className="th-sub">Định mức viên</span></th>
                                            <th className="tar">1 chiếc 180g<span className="th-sub">Định mức viên</span></th>
                                            <th className="tar col-150">Tổng {calcData.total150} bánh 150g<span className="th-sub">Tính riêng</span></th>
                                            <th className="tar col-180">Tổng {calcData.total180} bánh 180g<span className="th-sub">Tính riêng</span></th>
                                            <th className="tar col-total">TỔNG NHÂN CẢ MẺ<span className="th-sub">Toàn bộ</span></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <strong>Nhân bánh nướng ({calcData.totalNuong} chiếc)</strong>
                                                <div style={{ fontSize: '11px', color: '#7A6A5F' }}>
                                                    {calcData.nuong150} cái 150g (95g/viên) · {calcData.nuong180} cái 180g (115g/viên)
                                                </div>
                                            </td>
                                            <td className="tar">95.0g</td>
                                            <td className="tar">115.0g</td>
                                            <td className="tar col-150">
                                                <span className="recipe-gram-tag tint-150">{formatRoundGram(calcData.nuong150 * 95)}</span>
                                            </td>
                                            <td className="tar col-180">
                                                <span className="recipe-gram-tag tint-180">{formatRoundGram(calcData.nuong180 * 115)}</span>
                                            </td>
                                            <td className="tar col-total">
                                                <span className="recipe-gram-tag brand">{formatRoundGram((calcData.nuong150 * 95) + (calcData.nuong180 * 115))}</span>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <strong>Nhân bánh dẻo ({calcData.totalDeo} chiếc)</strong>
                                                <div style={{ fontSize: '11px', color: '#7A6A5F' }}>
                                                    {calcData.deo150} cái 150g (75g/viên) · {calcData.deo180} cái 180g (90g/viên)
                                                </div>
                                            </td>
                                            <td className="tar">75.0g</td>
                                            <td className="tar">90.0g</td>
                                            <td className="tar col-150">
                                                <span className="recipe-gram-tag tint-150">{formatRoundGram(calcData.deo150 * 75)}</span>
                                            </td>
                                            <td className="tar col-180">
                                                <span className="recipe-gram-tag tint-180">{formatRoundGram(calcData.deo180 * 90)}</span>
                                            </td>
                                            <td className="tar col-total">
                                                <span className="recipe-gram-tag brand">{formatRoundGram((calcData.deo150 * 75) + (calcData.deo180 * 90))}</span>
                                            </td>
                                        </tr>
                                        {currentFilling.eggQty > 0 && (
                                            <tr>
                                                <td>
                                                    <strong style={{ color: '#B8862F' }}>Lòng đỏ trứng muối nướng</strong>
                                                    <div style={{ fontSize: '11px', color: '#7A6A5F' }}>Theo đơn hàng thực tế</div>
                                                </td>
                                                <td className="tar">1 quả</td>
                                                <td className="tar">1 quả</td>
                                                <td className="tar col-150">—</td>
                                                <td className="tar col-180">—</td>
                                                <td className="tar col-total">
                                                    <span className="recipe-gram-tag gold">{currentFilling.eggQty} quả</span>
                                                </td>
                                            </tr>
                                        )}
                                        <tr className="total-row">
                                            <td><strong>TỔNG KHỐI LƯỢNG NHÂN CẦN SÊN / CÂN</strong></td>
                                            <td className="tar"><strong>—</strong></td>
                                            <td className="tar"><strong>—</strong></td>
                                            <td className="tar col-150"><strong>{formatRoundGram(calcData.fillingGram150)}</strong></td>
                                            <td className="tar col-180"><strong>{formatRoundGram(calcData.fillingGram180)}</strong></td>
                                            <td className="tar col-total">
                                                <strong style={{ fontSize: '16px', color: 'var(--red)' }}>
                                                    {formatRoundGram(calcData.totalFillingWeight)} (~{(calcData.totalFillingWeight / 1000).toFixed(2)} kg)
                                                </strong>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* Toast popup */}
            {toast && (
                <div className="recipe-toast">
                    <i className="fa-solid fa-circle-check" />
                    <span>{toast}</span>
                </div>
            )}
        </div>
    );
}
